import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom'
import { AdminLayout } from '@/widgets/admin-layout'
import { AppLayout, PageHeader } from '@/widgets/layout/ui/AppLayout'
import { ClientShell } from '@/widgets/layout/ui/ClientShell'
import {
  AdminAuditPage,
  AdminBalancePage,
  AdminCallPage,
  AdminCallsPage,
  AdminCompaniesPage,
  AdminCompanyPage,
  AdminSummaryPage,
} from '@/pages/admin'
import { LoginPage, RegisterPage } from '@/pages/auth'
import { DashboardPage } from '@/pages/dashboard'
import { ProfilePage } from '@/pages/profile'
import { HoursPage } from '@/pages/hours'
import { PromptPage } from '@/pages/prompt'
import { CompanyPage } from '@/pages/company'
import { TestCallPage } from '@/pages/test-call'
import { CallsPage } from '@/pages/calls'
import { CallPage } from '@/pages/call'
import { ClientPage } from '@/pages/client'
import { ClientEventsPage } from '@/pages/client-events'
import { CampaignsPage } from '@/pages/campaigns'
import { IntegrationsPage } from '@/pages/integrations'
import { CampaignPage } from '@/pages/campaign'
import { VoicePage } from '@/pages/voice'
import { SettingsPage } from '@/pages/settings'
import { AgendaPage } from '@/pages/agenda'
import { OnboardingPage } from '@/pages/onboarding'
import { NotFoundPage } from '@/pages/not-found'
import { SIPPage } from '@/pages/company/SIPPage'
import { PasswordForm } from '@/pages/company/PasswordForm'
import { RequireAdmin, RequireAuth, RequireGuest, RequireSetup } from './guards'

const router = createBrowserRouter([
  {
    element: <RequireGuest />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/register', element: <RegisterPage /> },
    ],
  },
  {
    element: <RequireAuth />,
    children: [
      // Админка не зависит от готовности клиентского проекта: суперадмин
      // должен открыть её даже у собственного ненастроенного аккаунта.
      {
        element: <RequireAdmin />,
        children: [
          {
            element: <AdminLayout />,
            children: [
              { path: '/admin', element: <AdminSummaryPage /> },
              { path: '/admin/balance', element: <AdminBalancePage /> },
              { path: '/admin/companies', element: <AdminCompaniesPage /> },
              { path: '/admin/companies/:id', element: <AdminCompanyPage /> },
              { path: '/admin/calls', element: <AdminCallsPage /> },
              { path: '/admin/calls/:id', element: <AdminCallPage /> },
              { path: '/admin/audit', element: <AdminAuditPage /> },
            ],
          },
        ],
      },
      {
        element: <ClientShell />,
        children: [
          // Онбординг живёт вне каркаса кабинета: это первый запуск на весь
          // экран, но при impersonation над ним всё равно остаётся жёлтая полоса.
          { path: '/onboarding', element: <OnboardingPage /> },
          { element: <AppLayout />, children: [{ path: '/telephony', element: <SIPPage /> }, { path: '/account', element: <><PageHeader title="Аккаунт и пароль" /><PasswordForm /></> }] },
          {
            element: <RequireSetup />,
            children: [
              {
                element: <AppLayout />,
                children: [
                  { path: '/', element: <DashboardPage /> },
                  { path: '/test-call', element: <TestCallPage /> },
                  { path: '/calls', element: <CallsPage /> },
                  { path: '/agenda', element: <AgendaPage /> },
                  { path: '/campaigns', element: <CampaignsPage /> },
                  { path: '/integrations', element: <IntegrationsPage /> },
                  { path: '/client-events', element: <ClientEventsPage /> },
                  { path: '/calls/:id', element: <CallPage /> },
                  { path: '/clients/:id', element: <ClientPage /> },
                  { path: '/campaigns/:id', element: <CampaignPage /> },
                  {
                    path: '/settings',
                    element: <SettingsPage />,
                    children: [
                      { index: true, element: <Navigate to="/settings/profile" replace /> },
                      { path: 'profile', element: <ProfilePage /> },
                      { path: 'voice', element: <VoicePage /> },
                      { path: 'hours', element: <HoursPage /> },
                      { path: 'prompt', element: <PromptPage /> },
                      { path: 'company', element: <CompanyPage /> },
                    ],
                  },
                  // Старые адреса сохранены для закладок и ссылок из переписки.
                  { path: '/profile', element: <Navigate to="/settings/profile" replace /> },
                  { path: '/voice', element: <Navigate to="/settings/voice" replace /> },
                  { path: '/hours', element: <Navigate to="/settings/hours" replace /> },
                  { path: '/prompt', element: <Navigate to="/settings/prompt" replace /> },
                  { path: '/company', element: <Navigate to="/settings/company" replace /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  { path: '/not-found', element: <NotFoundPage /> },
  // Старый адрес редактора остался в закладках и предыдущих сборках.
  // Отдельного редактора пока нет: сценарий меняется через настройщика.
  { path: '/settings/script', element: <Navigate to="/onboarding" replace /> },
  { path: '*', element: <NotFoundPage /> },
])

export function Router() {
  return <RouterProvider router={router} />
}
